import React, {
  useRef,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import styled from 'styled-components';

import { Input, UniversalSearch, Icon } from 'components';
import { useBreakpoint } from 'hooks';
import ReactDOM from 'react-dom';
import { LayoutContext } from 'contexts';
import TourListingContext from '../TourListingContext';
import TourEditContext from '../TourEditContext';
import MaxItemsExceededTooltip from '../MaxItemsExceededTooltip';
import { MAX_TOUR_STOPS } from '../constants';
import { TourModalType } from '../types';
import { useTemporaryStyle, useWindowHeight } from '../hooks';

const ListingAndStopSearch: React.FC = () => {
  const wrapperRef = useRef(null);
  const universalSearchRef = useRef(null);
  const [offset, setOffset] = useState(0);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSearchOpened, setIsSearchOpened] = useState(isSearchOpen);
  const {
    setModalType,
    setListingId,
    setSelectedTourItem,
    setListingModalState,
  } = useContext(TourListingContext);
  const { tourItems, addSelectedListingToTour, addSelectedStopToTour } =
    useContext(TourEditContext);
  const { topElementHeights } = useContext(LayoutContext);

  const { md } = useBreakpoint();

  const openTourListingModal = useCallback(
    (listingId: string) => {
      const existingTourItem = tourItems.find(
        ({ listingId: tourListingId }) =>
          tourListingId && tourListingId === listingId,
      );

      setModalType(TourModalType.LISTING);
      // Need them separately since listings that are not yet added will not have
      // TourItemContent, but only listingId -> handled from Universal Search
      setListingId(listingId);
      setSelectedTourItem(existingTourItem || null);
      setListingModalState(true, true);
    },
    [
      setListingId,
      setSelectedTourItem,
      setListingModalState,
      setModalType,
      tourItems,
    ],
  );

  const searchIsDisabled = tourItems.length >= MAX_TOUR_STOPS;

  useEffect(() => {
    if (wrapperRef.current) {
      const { top } = wrapperRef.current.getBoundingClientRect();
      setOffset(md ? top : topElementHeights.bbAssume);
    }
  }, [md, topElementHeights.bbAssume, isSearchOpen]);

  useEffect(() => setIsSearchOpened(isSearchOpen), [isSearchOpen]);

  useEffect(() => {
    if (isSearchOpened) universalSearchRef.current?.inputRef.current.focus();
  }, [isSearchOpened]);

  // this line fixed the problem on iOS where the resize doesn't trigger when height changes.
  const height = useWindowHeight(300);
  useTemporaryStyle(
    () => $('html')[0],
    !md &&
      (!isSearchOpened
        ? { height: `${height - 1}px` }
        : { height: `calc(100vh - 1px)` }),
    [height, isSearchOpened, md],
  );

  const searchContent = (
    <StyledSearchWrapper ref={wrapperRef} $md={md}>
      <UniversalSearch
        orgType="brokerage"
        searchFromTour
        customOffset={offset}
        customPlaceholder="Add listings or stops"
        addListingToTour={addSelectedListingToTour}
        addStopToTour={addSelectedStopToTour}
        searchIsDisabled={searchIsDisabled}
        openTourListingModal={openTourListingModal}
        onSearchClose={() => {
          if (isSearchOpened)
            // prevent closing on component mount
            setIsSearchOpen(false);
        }}
        ref={universalSearchRef}
      />
    </StyledSearchWrapper>
  );

  return (
    <>
      {md ? (
        <MaxItemsExceededTooltip totalStops={tourItems.length}>
          {searchContent}
        </MaxItemsExceededTooltip>
      ) : (
        <>
          {ReactDOM.createPortal(
            isSearchOpen && searchContent,
            document.getElementById('tour-edit-page-root'),
          )}
          <span
            onMouseDown={e => {
              if (searchIsDisabled) return;
              e.preventDefault();
              setIsSearchOpen(true);
            }}
          >
            <MaxItemsExceededTooltip totalStops={tourItems.length}>
              <div>
                <StyledDummyInput
                  prefix={
                    <StyledIconWrapper>
                      <Icon
                        type="search"
                        height={20}
                        width={20}
                        color="black"
                      />
                    </StyledIconWrapper>
                  }
                  placeholder="Add listings or stops"
                  onClick={e => {
                    e.stopPropagation();
                  }}
                />
              </div>
            </MaxItemsExceededTooltip>
          </span>
        </>
      )}
    </>
  );
};

export default ListingAndStopSearch;

const StyledDummyInput = styled(Input)`
  .ant-input-affix-wrapper-disabled,
  .ant-input[disabled] {
    cursor: text;
  }
  height: 36px;
  margin: 16px 12px -12px 12px;
`;

const StyledIconWrapper = styled.div`
  width: 22px;
  display: flex;
`;

const StyledSearchWrapper = styled.div<{ $md: boolean }>`
  ${({ $md }) =>
    !$md &&
    'position: absolute; top: -1000px;'}; // move it off screen so that cursor doesn't jump around
  height: 36px;
  margin: ${({ $md }) => ($md ? '15px 24px 0 24px' : '15px 12px 0 12px')};
`;
